/**
 * TaskRow — oppgaveraden (#152, mangler i MAPPING.md §3).
 *
 * Glasskort med radius 20, 12 px padding og tre deler på én linje:
 *   - `icon`: den tonede ikonflisen, 44 px (ChoreIcon eller en egen flis)
 *   - tittel (`text-body`, 15 px / 700) og valgfri `meta` (12 px, ink-2) som fyller
 *     bredden og kuttes med ellipse i stedet for å dytte handlingen
 *   - `action`: en spalte med FAST bredde 96 px. Den faste bredden er
 *     regelen «Ta flytter aldri kortet» (design/HANDOFF.md): knappen kan
 *     bytte fra «Ta» til venting til «Fullfør» uten at raden endrer form.
 *
 * Uten `action` er det ingen spalte — radene på Godkjenn, Maler og
 * Lommebok bruker samme layout uten handling. `children` havner under
 * raden (f.eks. en tilbakemelding fra en voksen).
 *
 * ChoreCard og HomeworkCard står på denne raden fra steg 4b (#154). Andre
 * attributter (`data-variant`, `aria-busy`) går videre til `<article>`.
 */
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { GLASS_FILL } from './Card';

interface TaskRowProps extends Omit<ComponentPropsWithoutRef<'article'>, 'title'> {
  icon: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  action?: ReactNode;
  /**
   * Kantfargen, som erstatter glassets `border-card-line` i stedet for å
   * konkurrere med den (to kantfarger på samme element har ingen fast
   * rekkefølge i Tailwind). Oppgavekortene bruker lilla for «din» (#154).
   */
  line?: string | undefined;
  children?: ReactNode;
  className?: string | undefined;
  'data-testid'?: string;
}

export function TaskRow({
  icon,
  title,
  meta,
  action,
  line = 'border-card-line',
  children,
  className,
  'data-testid': testId = 'task-row',
  ...rest
}: TaskRowProps): ReactNode {
  return (
    <article
      {...rest}
      data-testid={testId}
      className={[
        'flex flex-col gap-2.5 rounded-row border p-3 shadow-md',
        GLASS_FILL,
        line,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center" aria-hidden>
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="m-0 truncate text-body font-bold text-ink">{title}</h3>
          {meta ? <p className="m-0 mt-0.5 truncate text-caption text-ink-2">{meta}</p> : null}
        </div>
        {action ? (
          <div data-testid={`${testId}-action`} className="flex w-24 shrink-0 justify-end [&>*]:w-full">
            {action}
          </div>
        ) : null}
      </div>
      {children}
    </article>
  );
}
